// The lens top bar's measured height, published on the document root as
//   --lens-topbar-h      the raw measured height
//   --lens-topbar-inset  12px bar top + height + 16px gap — the ready-made
//                        clearance every below-the-bar reader wants (the
//                        right aside's top in Main.jsx, /threads' page
//                        padding). Derived HERE so the formula has one home
//                        and readers cannot drift. The 16px gap matches
//                        every other gap on the top line (LensShell's
//                        TOOLBAR_GAP, the tile column, the viewport edges)
//                        — birr's r4 even-spacing ruling, up from 8.
// The floating sidebar toggle (Main.jsx) centers against the raw height.
//
// Extracted from LensShell so /threads' bar (ThreadsTopBar) can publish too.
// The two publishers never mount at the same time: LensShell mounts only on
// LENS_ROUTE_KEYS routes and threads is not one. The vars are deliberately
// NOT removed on unmount (birr 2026-08-17, NAVBAR_REMOVAL_PLAN §3.5):
// non-topbar routes keep the last measured height so the toggle is the same
// size everywhere. The localStorage stash covers the cold start where a
// non-topbar route paints before any bar has measured — seedLensTopbarVar()
// reads it at module init (Main.jsx); before this extraction the stash was
// write-only and the seeding the comments promised did not exist.
//
// useLayoutEffect, not useEffect (review 2026-08-21): the measure and the
// publish must land BEFORE the browser paints, or the first frame of a page
// mount computes clearances from the previous page's value (a wrapped
// two-row bar leaving ~100px behind) and the layout visibly snaps a frame
// later.
import { useLayoutEffect, useState } from "react";

const H_VAR = "--lens-topbar-h";
const INSET_VAR = "--lens-topbar-inset";
const STASH_KEY = "lens-topbar-h";

// Keep in sync with LensToolbar's top-3 (12px); the gap below the bar is the
// system-wide 16 (matches LensShell's TOOLBAR_GAP).
const BAR_TOP_PX = 12;
const BAR_GAP_PX = 16;

// Sanity bounds: a bar is one to a few control rows, never a page. A stash
// outside them is garbage (another app on localhost, a bad write) — ignore it
// and let the CSS fallbacks serve until a bar measures.
const MIN_SANE = 24;
const MAX_SANE = 400;

const publish = (height) => {
  const root = document.documentElement;
  root.style.setProperty(H_VAR, `${height}px`);
  root.style.setProperty(INSET_VAR, `${BAR_TOP_PX + height + BAR_GAP_PX}px`);
};

export function seedLensTopbarVar() {
  try {
    const stashed = Number(window.localStorage.getItem(STASH_KEY));
    if (Number.isFinite(stashed) && stashed >= MIN_SANE && stashed <= MAX_SANE) {
      publish(stashed);
    }
  } catch {
    /* storage blocked: the CSS var fallbacks cover the cold start */
  }
}

export default function useLensTopbarVar(barRef, { onChange } = {}) {
  const [height, setHeight] = useState(56);

  useLayoutEffect(() => {
    publish(height);
    try {
      window.localStorage.setItem(STASH_KEY, String(height));
    } catch {
      /* storage blocked: the live vars still serve this session */
    }
  }, [height]);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const next = el.offsetHeight;
      setHeight(next);
      onChange?.(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // barRef is a ref (stable); a bar that wraps to two rows re-fires the
    // observer, which is how the vars track the wrap live.
  }, [barRef, onChange]);

  return height;
}
